/**
 * Club-scoped spreadsheet nicknames for paste-roster matching (host/admin via RLS).
 * Unique (club_id, alias_normalized): first insert wins if two players share a nickname.
 */
import { supabase } from '../utils/supabase';
import { normalizeNameKey, playerNameParts } from '../utils/rosterPasteMatch';

export const PASTE_ALIAS_MIN_LENGTH = 2;
export const PASTE_ALIAS_MAX_PER_PLAYER = 25;

const PLAYER_FIELDS = 'id, first_name, last_name, gender, is_guest';

export function pasteAliasMapFromRows(rows) {
  const map = new Map();
  (rows || []).forEach((row) => {
    if (row?.alias_normalized && row?.player_id) {
      map.set(row.alias_normalized, row.player_id);
    }
  });
  return map;
}

/**
 * True when the pasted line is worth storing as a nickname for this player.
 */
export function shouldSavePasteAlias(parsed, player) {
  const key = normalizeNameKey(parsed?.normalized || parsed?.raw);
  if (!key || key.length < PASTE_ALIAS_MIN_LENGTH || !player) return false;
  const parts = playerNameParts(player);
  return key !== parts.first && key !== parts.last && key !== parts.full && key !== parts.reversed;
}

export async function fetchPasteAliasesByClub(clubId) {
  if (!clubId) return { success: true, map: new Map() };
  const { data, error } = await supabase
    .from('club_player_paste_aliases')
    .select('player_id, alias_normalized, alias_display')
    .eq('club_id', clubId);

  if (error) {
    console.error('[fetchPasteAliasesByClub]', error);
    return { success: false, error: error.message, map: new Map() };
  }
  return { success: true, map: pasteAliasMapFromRows(data) };
}

/**
 * Insert an alias. Unique conflicts (including first-wins vs another player) are ignored.
 */
export async function savePasteAlias({ clubId, player, parsed }) {
  if (!clubId || !player?.id || !shouldSavePasteAlias(parsed, player)) {
    return { success: true, saved: false };
  }
  const key = normalizeNameKey(parsed?.normalized || parsed?.raw);
  const display = String(parsed?.raw || key).trim();

  const { error } = await supabase.from('club_player_paste_aliases').insert({
    club_id: clubId,
    player_id: player.id,
    alias_normalized: key,
    alias_display: display,
  });

  if (error) {
    if (error.code === '23505') {
      return { success: true, saved: false, conflict: true };
    }
    console.error('[savePasteAlias]', error);
    return { success: false, error: error.message, saved: false };
  }
  return { success: true, saved: true };
}

export function preparePasteAliasInput(aliasText, player) {
  const display = String(aliasText || '').trim();
  const key = normalizeNameKey(display);
  if (!key || key.length < PASTE_ALIAS_MIN_LENGTH) {
    return { ok: false, error: 'Nicknames need at least 2 characters.' };
  }
  if (player && !shouldSavePasteAlias({ raw: display, normalized: key }, player)) {
    return { ok: false, error: 'That’s already this player’s real name — no nickname needed.' };
  }
  return { ok: true, key, display };
}

function mapAliasRow(row) {
  const player = row?.players || null;
  return {
    id: row.id,
    club_id: row.club_id,
    player_id: row.player_id,
    alias_normalized: row.alias_normalized,
    alias_display: row.alias_display,
    created_at: row.created_at,
    player: player ? { ...player, id: player.id || row.player_id } : { id: row.player_id },
  };
}

export async function fetchPasteAliasRowsByClub(clubId) {
  if (!clubId) return { success: true, rows: [] };
  const { data, error } = await supabase
    .from('club_player_paste_aliases')
    .select(`id, club_id, player_id, alias_normalized, alias_display, created_at, players:player_id (${PLAYER_FIELDS})`)
    .eq('club_id', clubId)
    .order('alias_normalized', { ascending: true });

  if (error) {
    console.error('[fetchPasteAliasRowsByClub]', error);
    return { success: false, error: error.message, rows: [] };
  }
  return { success: true, rows: (data || []).map(mapAliasRow) };
}

export async function fetchClubPlayersForPasteAliases(clubId) {
  if (!clubId) return { success: true, players: [] };
  const { data, error } = await supabase
    .from('players_club')
    .select(`player_id, club_role, is_active, players:player_id (${PLAYER_FIELDS})`)
    .eq('club_id', clubId)
    .eq('is_active', true);

  if (error) {
    console.error('[fetchClubPlayersForPasteAliases]', error);
    return { success: false, error: error.message, players: [] };
  }

  const players = (data || [])
    .map((row) => {
      const player = row.players;
      if (!player?.id) return null;
      return {
        ...player,
        id: player.id,
        is_guest: Boolean(player.is_guest || row.club_role === 'guest'),
      };
    })
    .filter(Boolean)
    .sort((a, b) =>
      `${a.first_name || ''} ${a.last_name || ''}`.localeCompare(
        `${b.first_name || ''} ${b.last_name || ''}`,
        undefined,
        { sensitivity: 'base' }
      )
    );

  return { success: true, players };
}

async function findAliasOwner(clubId, aliasNormalized) {
  const { data, error } = await supabase
    .from('club_player_paste_aliases')
    .select(`player_id, alias_display, players:player_id (${PLAYER_FIELDS})`)
    .eq('club_id', clubId)
    .eq('alias_normalized', aliasNormalized)
    .maybeSingle();

  if (error || !data) return null;
  return mapAliasRow({ ...data, club_id: clubId, alias_normalized: aliasNormalized });
}

async function countAliasesForPlayer(clubId, playerId) {
  const { count, error } = await supabase
    .from('club_player_paste_aliases')
    .select('id', { count: 'exact', head: true })
    .eq('club_id', clubId)
    .eq('player_id', playerId);

  if (error) {
    console.error('[countAliasesForPlayer]', error);
    return 0;
  }
  return count || 0;
}

function conflictMessage(aliasDisplay, owner) {
  const name = owner?.player
    ? [owner.player.first_name, owner.player.last_name].filter(Boolean).join(' ').trim()
    : '';
  if (name) {
    return `“${aliasDisplay}” already maps to ${name}. Reassign it from the list, or pick a different nickname.`;
  }
  return `“${aliasDisplay}” already maps to another player in this club.`;
}

export async function createPasteAliasAdmin({ clubId, player, aliasText }) {
  const prepared = preparePasteAliasInput(aliasText, player);
  if (!clubId || !player?.id) {
    return { success: false, error: 'Club or player is missing.' };
  }
  if (!prepared.ok) return { success: false, error: prepared.error };

  const existingCount = await countAliasesForPlayer(clubId, player.id);
  if (existingCount >= PASTE_ALIAS_MAX_PER_PLAYER) {
    return { success: false, error: `A player can have at most ${PASTE_ALIAS_MAX_PER_PLAYER} paste aliases.` };
  }

  const { data, error } = await supabase
    .from('club_player_paste_aliases')
    .insert({
      club_id: clubId,
      player_id: player.id,
      alias_normalized: prepared.key,
      alias_display: prepared.display,
    })
    .select(`id, club_id, player_id, alias_normalized, alias_display, created_at, players:player_id (${PLAYER_FIELDS})`)
    .single();

  if (error) {
    if (error.code === '23505') {
      const owner = await findAliasOwner(clubId, prepared.key);
      return { success: false, conflict: true, error: conflictMessage(prepared.display, owner) };
    }
    console.error('[createPasteAliasAdmin]', error);
    return { success: false, error: error.message };
  }
  return { success: true, row: mapAliasRow(data) };
}

export async function updatePasteAliasAdmin({ clubId, aliasId, player, aliasText }) {
  if (!clubId || !aliasId || !player?.id) {
    return { success: false, error: 'Club, nickname, or player is missing.' };
  }
  const prepared = preparePasteAliasInput(aliasText, player);
  if (!prepared.ok) return { success: false, error: prepared.error };

  const { data: current, error: currentError } = await supabase
    .from('club_player_paste_aliases')
    .select('id, player_id')
    .eq('club_id', clubId)
    .eq('id', aliasId)
    .maybeSingle();

  if (currentError || !current) {
    return { success: false, error: currentError?.message || 'Nickname not found.' };
  }

  if (current.player_id !== player.id) {
    const existingCount = await countAliasesForPlayer(clubId, player.id);
    if (existingCount >= PASTE_ALIAS_MAX_PER_PLAYER) {
      return { success: false, error: `A player can have at most ${PASTE_ALIAS_MAX_PER_PLAYER} paste aliases.` };
    }
  }

  const { data, error } = await supabase
    .from('club_player_paste_aliases')
    .update({
      player_id: player.id,
      alias_normalized: prepared.key,
      alias_display: prepared.display,
    })
    .eq('club_id', clubId)
    .eq('id', aliasId)
    .select(`id, club_id, player_id, alias_normalized, alias_display, created_at, players:player_id (${PLAYER_FIELDS})`)
    .single();

  if (error) {
    if (error.code === '23505') {
      const owner = await findAliasOwner(clubId, prepared.key);
      return { success: false, conflict: true, error: conflictMessage(prepared.display, owner) };
    }
    console.error('[updatePasteAliasAdmin]', error);
    return { success: false, error: error.message };
  }
  return { success: true, row: mapAliasRow(data) };
}

export async function deletePasteAliasAdmin({ clubId, aliasId }) {
  if (!clubId || !aliasId) return { success: false, error: 'Club or nickname is missing.' };
  const { error } = await supabase
    .from('club_player_paste_aliases')
    .delete()
    .eq('club_id', clubId)
    .eq('id', aliasId);

  if (error) {
    console.error('[deletePasteAliasAdmin]', error);
    return { success: false, error: error.message };
  }
  return { success: true };
}
